/**
 * TherapistParentLinkRequestsCard
 * -------------------------------------------------------------------
 * Surfaces parent-initiated pending links in the therapist's
 * dashboard. Each row has Approve + Decline buttons that hit
 * POST /therapist/parent-links/{id}/(approve|decline).
 *
 * Sep 2026 — production bug reported by user:
 *     "There's nowhere for me as the therapist to accept it and link"
 * The `parent_link_request` notification landed in the bell, but no
 * inbox existed for the actual approve action.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Check, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function TherapistParentLinkRequestsCard() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState(new Set());

  const fetchPending = async () => {
    try {
      const { data } = await axios.get(`${API}/therapist/parent-links`, auth());
      const rows = (data.links || []).filter(
        (l) => l.status === 'pending' && l.initiated_by === 'parent',
      );
      setPending(rows);
    } catch (e) {
      // Silent — this card is best-effort.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, []);

  const respond = async (linkId, action) => {
    setBusyIds((prev) => new Set(prev).add(linkId));
    try {
      await axios.post(`${API}/therapist/parent-links/${linkId}/${action}`, {}, auth());
      toast.success(action === 'approve' ? 'Connected — the parent has been notified.' : 'Request declined.');
      // Optimistically remove
      setPending((prev) => prev.filter((l) => l.id !== linkId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update — try again.');
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(linkId);
        return next;
      });
    }
  };

  if (loading) return null;
  if (pending.length === 0) return null;

  return (
    <Card
      className="p-5 md:p-6 mb-8 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50/60 to-orange-50/60"
      data-testid="therapist-parent-link-requests"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0">
          <UserPlus className="w-5 h-5 text-amber-700" />
        </div>
        <div>
          <h3 className="text-lg font-serif text-primary-900">
            {pending.length === 1
              ? 'A parent wants to link with you'
              : `${pending.length} parents want to link with you`}
          </h3>
          <p className="text-sm text-stone-600">
            They've invited you via PlayFirst Parent. Accept to connect and start sharing focus goals.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {pending.map((l) => {
          const busy = busyIds.has(l.id);
          const name = l.parent_name || l.parent_email;
          return (
            <div
              key={l.id}
              className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-2xl bg-white/70 border border-amber-100 p-3"
              data-testid={`parent-link-request-${l.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-stone-800 truncate">
                    {name}
                  </span>
                  {l.parent_name && (
                    <span className="text-xs text-stone-500 truncate">
                      {l.parent_email}
                    </span>
                  )}
                  <Badge variant="outline" className="text-[11px] rounded-full">
                    {l.created_at ? new Date(l.created_at).toLocaleDateString() : ''}
                  </Badge>
                </div>
                <p className="text-xs text-stone-500 mt-0.5">
                  Wants to connect on PlayFirst Parent to receive your focus goals in daily play ideas.
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  onClick={() => respond(l.id, 'approve')}
                  disabled={busy}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`approve-parent-link-${l.id}`}
                >
                  {busy
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <><Check className="w-4 h-4 mr-1.5" /> Accept</>}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => respond(l.id, 'decline')}
                  disabled={busy}
                  className="rounded-full border-stone-300"
                  data-testid={`decline-parent-link-${l.id}`}
                >
                  <X className="w-4 h-4 mr-1.5" /> Decline
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
